import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import {
  FileCode2,
  History,
  LogOut,
  Settings as SettingsIcon,
  ShieldCheck,
  Shuffle,
} from "lucide-react";
import { ParsePage } from "./pages/ParsePage";
import { TranslatePage } from "./pages/TranslatePage";
import { ValidatePage } from "./pages/ValidatePage";
import { LoginPage } from "./pages/LoginPage";
import { HistoryPage } from "./pages/HistoryPage";
import { SettingsPage } from "./pages/SettingsPage";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { LicenseBadge } from "./components/LicenseBadge";
import { Stepper } from "./components/Stepper";
import { TourLauncher, TourOverlay, tourForPath, useAutoStartTour } from "./components/tour";
import { clearAuthToken, isAuthenticated } from "./lib/api";

export function App() {
  return (
    <div className="grid min-h-screen grid-rows-[auto,auto,1fr] bg-bridge-fog">
      <Header />
      <Stepper />
      <main className="min-h-0">
        <Routes>
          <Route path="/" element={<Navigate to="/parse" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/parse"
            element={
              <ProtectedRoute>
                <ParsePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/translate"
            element={
              <ProtectedRoute>
                <TranslatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/validate"
            element={
              <ProtectedRoute>
                <ValidatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <HistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>
      <RouteTourBinder />
      <TourOverlay />
    </div>
  );
}

function RouteTourBinder() {
  const { pathname } = useLocation();
  const tour = tourForPath(pathname);
  if (!tour) return null;
  return <BoundTour key={tour.id} />;
}

function BoundTour() {
  const { pathname } = useLocation();
  const tour = tourForPath(pathname)!;
  useAutoStartTour(tour);
  return null;
}

function Header() {
  const navigate = useNavigate();
  const authed = isAuthenticated();

  function signOut() {
    clearAuthToken();
    navigate("/login", { replace: true });
  }

  return (
    <header className="sticky top-0 z-20 border-b border-bridge-slate/25 bg-white/85 backdrop-blur">
      <div className="flex w-full items-center gap-6 px-6 py-2.5">
        <a href="/" className="flex items-center gap-2.5 font-semibold text-bridge-ink">
          <Logo />
          <div className="leading-tight">
            <div className="font-display text-[15px]">DatumCraft Bridge</div>
            <div className="text-2xs font-normal text-bridge-slate">
              Cognos to Power BI / Tableau / Qlik — translation with confidence
            </div>
          </div>
        </a>
        {authed && (
          <nav aria-label="primary" data-tour-id="nav" className="flex gap-1 text-sm">
            <NavItem to="/parse" icon={<FileCode2 size={14} />}>Parse</NavItem>
            <NavItem to="/translate" icon={<Shuffle size={14} />}>Translate</NavItem>
            <NavItem to="/validate" icon={<ShieldCheck size={14} />}>Validate</NavItem>
            <NavItem to="/history" icon={<History size={14} />}>History</NavItem>
            <NavItem to="/settings" icon={<SettingsIcon size={14} />}>Settings</NavItem>
          </nav>
        )}
        <div className="ml-auto flex items-center gap-2 text-2xs text-bridge-slate">
          <LicenseBadge />
          <TourLauncher />
          {authed && (
            <button
              type="button"
              onClick={signOut}
              className="inline-flex items-center gap-1 rounded-md border border-bridge-slate/30 bg-white px-2 py-0.5 font-medium text-bridge-ink hover:bg-bridge-mist"
              title="Sign out"
            >
              <LogOut size={11} />
              Sign out
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

function NavItem({
  to,
  icon,
  children,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 font-medium transition-colors ${
          isActive
            ? "bg-bridge-primary text-white shadow-sm"
            : "text-bridge-ink hover:bg-bridge-mist"
        }`
      }
    >
      <span aria-hidden>{icon}</span>
      <span>{children}</span>
    </NavLink>
  );
}

// Product glyph — two arrows in opposite directions (the migration
// motion). Mirrors `GlyphBridge` from the website's brand system at
// `website/app/brand/products/glyphs.tsx`. Top arrow uses currentColor
// so it follows the parent text-* class; bottom arrow stays brand
// violet #7C5CFF.
function Logo() {
  return (
    <span
      className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-bridge-ink text-white shadow-sm"
      aria-hidden
    >
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 9 H17" stroke="currentColor" strokeWidth={1.6} />
        <path d="M14 6 l3 3 -3 3" stroke="currentColor" strokeWidth={1.6} />
        <path d="M20 15 H7" stroke="#7C5CFF" strokeWidth={1.6} />
        <path d="M10 18 l-3 -3 3 -3" stroke="#7C5CFF" strokeWidth={1.6} />
      </svg>
    </span>
  );
}
